import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles,
  CreditCard,
  TrendingUp,
  AlertCircle,
  Info,
  Loader2,
  ExternalLink,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/* Phase 9 — Billing panel rendered inside the Settings modal.
 *
 * Visibility: owners + admins can see this panel. Mutations (subscribe /
 * topup / cancel / portal) are gated to owners only by the backend; admins
 * see the same data read-only.
 */

function fmtUsd(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const STATUS_LABEL = {
  active: { label: "Active", tone: "ok" },
  trialing: { label: "Trialing", tone: "info" },
  past_due: { label: "Past due", tone: "warn" },
  canceled: { label: "Canceled", tone: "muted" },
  incomplete: { label: "Incomplete", tone: "warn" },
  incomplete_expired: { label: "Expired", tone: "muted" },
  unpaid: { label: "Unpaid", tone: "danger" },
};

export default function BillingPanel({ workspace }) {
  const isOwner = workspace?.role === "owner";
  const isAdminOrOwner = isOwner || workspace?.role === "admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // "subscribe-team" | "topup-1000" | "cancel" | "portal"
  const [confirmPlanChange, setConfirmPlanChange] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState(2500);
  const [customTopup, setCustomTopup] = useState("");

  const load = useCallback(async () => {
    if (!workspace?.id || !isAdminOrOwner) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/workspaces/${workspace.id}/billing`
      );
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [workspace?.id, isAdminOrOwner]);

  useEffect(() => {
    load();
  }, [load]);

  // Handle return from Stripe Checkout — query params on workspace URL
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const topupParam = params.get("topup");
    const subParam = params.get("subscribe");
    if (topupParam === "success") {
      toast.success("Top-up complete — refreshing balance…");
      const t = setTimeout(load, 1200);
      cleanQueryParam(["topup"]);
      return () => clearTimeout(t);
    }
    if (topupParam === "canceled") {
      toast("Top-up canceled.");
      cleanQueryParam(["topup"]);
    }
    if (subParam === "success") {
      toast.success("Subscription activated — refreshing…");
      const t = setTimeout(load, 1200);
      cleanQueryParam(["subscribe"]);
      return () => clearTimeout(t);
    }
    if (subParam === "canceled") {
      toast("Subscription change canceled.");
      cleanQueryParam(["subscribe"]);
    }
    return undefined;
  }, [load]);

  const currentPlanCfg = useMemo(
    () =>
      (data?.plan_options || []).find((p) => p.plan === data?.plan) || null,
    [data]
  );
  const stripeState = data?.stripe || {};
  const isSubscribed = ["active", "trialing", "past_due"].includes(
    stripeState.subscription_status
  );
  const isSuspendedForPayment =
    data?.suspended && data?.suspended_reason === "payment_failed";

  if (!isAdminOrOwner) return null;
  if (loading && !data) {
    return (
      <section
        className="space-y-3"
        data-testid="settings-section-billing"
      >
        <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
          Billing
        </h3>
        <p className="font-sans text-sm text-[#7A7571] flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" strokeWidth={2} />
          Loading billing details…
        </p>
      </section>
    );
  }
  if (!data) return null;

  const startSubscribe = async (plan) => {
    if (!isOwner) {
      toast.error("Only the workspace owner can change the plan.");
      return;
    }
    setPending(`subscribe-${plan}`);
    try {
      const origin = window.location.origin;
      const here = `${origin}/w/${workspace.slug}`;
      const { data: resp } = await api.post(
        `/workspaces/${workspace.id}/billing/subscribe`,
        {
          plan,
          success_url: `${here}?subscribe=success`,
          cancel_url: `${here}?subscribe=canceled`,
        }
      );
      if (resp.handled_inline) {
        toast.success(`Plan switched to ${plan}.`);
        await load();
      } else if (resp.checkout_url) {
        window.location.assign(resp.checkout_url);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPending(null);
      setConfirmPlanChange(null);
    }
  };

  const startTopup = async (amount) => {
    if (!isOwner) {
      toast.error("Only the workspace owner can top up.");
      return;
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 500 || amt > 50000) {
      toast.error("Amount must be between $5 and $500.");
      return;
    }
    setPending(`topup-${amt}`);
    try {
      const origin = window.location.origin;
      const here = `${origin}/w/${workspace.slug}`;
      const { data: resp } = await api.post(
        `/workspaces/${workspace.id}/billing/topup`,
        {
          amount_cents: Math.round(amt),
          success_url: `${here}?topup=success`,
          cancel_url: `${here}?topup=canceled`,
        }
      );
      if (resp.checkout_url) {
        window.location.assign(resp.checkout_url);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPending(null);
      setTopupOpen(false);
    }
  };

  const startCancel = async () => {
    setPending("cancel");
    try {
      const { data: resp } = await api.post(
        `/workspaces/${workspace.id}/billing/cancel`,
        { at_period_end: true, reason: cancelReason || null }
      );
      if (resp.cancel_at_period_end) {
        toast.success(
          `Will cancel on ${fmtDate(
            stripeState.subscription_current_period_end
          )}.`
        );
      } else {
        toast.success("Subscription canceled.");
      }
      setConfirmCancel(false);
      setCancelReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPending(null);
    }
  };

  const openPortal = async () => {
    setPending("portal");
    try {
      const origin = window.location.origin;
      const { data: resp } = await api.post(
        `/workspaces/${workspace.id}/billing/portal`,
        { return_url: `${origin}/w/${workspace.slug}` }
      );
      if (resp.portal_url) {
        window.open(resp.portal_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPending(null);
    }
  };

  return (
    <section
      className="space-y-4"
      data-testid="settings-section-billing"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
          Billing
        </h3>
        {!isOwner ? (
          <span
            data-testid="billing-readonly-pill"
            className="font-sans text-[10px] uppercase tracking-[0.15em] text-[#A5A29E] font-semibold"
          >
            Read-only · ask the owner
          </span>
        ) : null}
      </div>

      {/* Phase 12 — Grandfathered workspace tester notice. Owner/Admin only;
          members never see Billing at all. Sits above all other Billing
          surfaces so superadmins (and grandfathered workspace owners)
          immediately understand why no upgrade buttons appear here. */}
      {data?.plan === "grandfathered" ? (
        <div
          data-testid="billing-grandfathered-notice"
          className="rounded-lg border border-[#DCD7CE] bg-[#F5F0E5] px-4 py-3 flex items-start gap-3"
        >
          <Info
            size={14}
            className="text-[#7A7571] shrink-0 mt-0.5"
            strokeWidth={2}
          />
          <p className="font-sans text-xs text-[#5C5752] leading-relaxed">
            This workspace is on a grandfathered internal plan with no
            billing limits. To test the paid-plan upgrade flow,{" "}
            <strong>create a new workspace</strong> from the workspace rail
            (the <code className="font-mono text-[11px]">+</code> button) —
            new workspaces start on Solo Free where you can test the upgrade
            flow with Stripe test cards (
            <code className="font-mono text-[11px]">4242 4242 4242 4242</code>
            ) and/or the{" "}
            <code className="font-mono text-[11px]">BRAIN-TESTER-2026</code>{" "}
            promo code.
          </p>
        </div>
      ) : null}

      {/* Payment-failed suspension banner */}
      {isSuspendedForPayment ? (
        <div
          data-testid="billing-payment-failed-banner"
          className="rounded-lg border border-[#A8351F]/45 bg-[#F8D6CE] p-4 flex items-start gap-3"
        >
          <AlertCircle
            size={16}
            className="text-[#A8351F] shrink-0 mt-0.5"
            strokeWidth={2}
          />
          <div className="flex-1 min-w-0">
            <p className="font-sans text-sm font-medium text-[#5E1812] mb-1">
              We couldn&apos;t charge your card.
            </p>
            <p className="font-sans text-xs text-[#5E1812] leading-relaxed">
              This workspace is suspended until the next invoice is paid.
              Update your payment method to restore access.
            </p>
            {isOwner ? (
              <button
                onClick={openPortal}
                disabled={pending === "portal"}
                data-testid="billing-payment-failed-update-button"
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-sans text-sm font-medium bg-[#A8351F] text-white hover:bg-[#8C3A1F] transition-colors disabled:opacity-50"
              >
                {pending === "portal" ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <CreditCard size={13} strokeWidth={2} />
                )}
                Update payment method
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Current plan + subscription card */}
      <div
        className="rounded-xl border border-[#DCD7CE] bg-white p-4"
        data-testid="billing-plan-card"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-1">
              Current plan
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="font-serif text-2xl text-[#2C2A29] tracking-tight"
                data-testid="billing-current-plan-name"
              >
                {currentPlanCfg?.display_name || data.plan}
              </span>
              {currentPlanCfg?.monthly_price_cents ? (
                <span className="font-mono text-sm text-[#7A7571]">
                  · {fmtUsd(currentPlanCfg.monthly_price_cents)}/mo
                </span>
              ) : null}
              {stripeState.subscription_status ? (
                <StatusPill status={stripeState.subscription_status} />
              ) : null}
              {stripeState.cancel_at_period_end ? (
                <span
                  data-testid="billing-cancel-pending-pill"
                  className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FAF1D1] text-[#5E4012]"
                >
                  Cancels {fmtDate(stripeState.subscription_current_period_end)}
                </span>
              ) : null}
            </div>
            {stripeState.subscription_current_period_end &&
            !stripeState.cancel_at_period_end ? (
              <p className="font-sans text-xs text-[#A5A29E] mt-1">
                Renews{" "}
                {fmtDate(stripeState.subscription_current_period_end)}
              </p>
            ) : null}
          </div>
        </div>

        {/* Plan switch CTAs */}
        <div
          className="flex flex-wrap gap-2 mt-3"
          data-testid="billing-plan-options"
        >
          {data.plan_options
            .filter((p) => p.plan !== "free")
            .map((p) => {
              const isCurrent = p.plan === data.plan;
              const cta = isCurrent
                ? "Current plan"
                : isSubscribed
                ? `Switch to ${p.display_name}`
                : `Upgrade to ${p.display_name}`;
              const disabled =
                !isOwner ||
                !p.can_switch_to ||
                pending === `subscribe-${p.plan}`;
              return (
                <button
                  key={p.plan}
                  data-testid={`billing-plan-cta-${p.plan}`}
                  disabled={disabled}
                  onClick={() => {
                    if (isSubscribed) setConfirmPlanChange(p);
                    else startSubscribe(p.plan);
                  }}
                  title={p.reason_blocked || undefined}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-sans text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isCurrent
                      ? "border border-[#DCD7CE] bg-[#EFEAE0] text-[#7A7571] cursor-default"
                      : "bg-[#2C2A29] text-white hover:bg-[#1A1918]"
                  }`}
                >
                  {pending === `subscribe-${p.plan}` ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Sparkles size={13} strokeWidth={2} />
                  )}
                  {cta}
                  {!isCurrent ? (
                    <span className="font-mono text-xs opacity-70">
                      {fmtUsd(p.monthly_price_cents)}/mo
                    </span>
                  ) : null}
                </button>
              );
            })}

          {isSubscribed && !stripeState.cancel_at_period_end ? (
            <button
              data-testid="billing-cancel-button"
              disabled={!isOwner || pending === "cancel"}
              onClick={() => setConfirmCancel(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-sans text-sm text-[#8C3A1F] hover:bg-[#8C3A1F]/10 transition-colors disabled:opacity-50"
            >
              Cancel subscription
            </button>
          ) : null}
        </div>
      </div>

      {/* Prepaid balance card */}
      <div
        className="rounded-xl border border-[#DCD7CE] bg-white p-4"
        data-testid="billing-balance-card"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-1">
              Prepaid credit balance
            </p>
            <p
              className="font-serif text-3xl text-[#2C2A29] tracking-tight"
              data-testid="billing-balance-amount"
            >
              {fmtUsd(data.prepaid_balance_cents)}
            </p>
            <p className="font-sans text-xs text-[#A5A29E] mt-1">
              Used after the included monthly allowance is exhausted.
            </p>
          </div>
          {isOwner ? (
            <button
              data-testid="billing-topup-open-button"
              onClick={() => setTopupOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-sans text-sm font-medium bg-[#D96C4A] text-white hover:bg-[#8C3A1F] transition-colors"
            >
              <TrendingUp size={13} strokeWidth={2} />
              Top up
            </button>
          ) : null}
        </div>
      </div>

      {/* Self-serve portal link */}
      {stripeState.has_customer && isOwner ? (
        <button
          data-testid="billing-portal-link"
          onClick={openPortal}
          disabled={pending === "portal"}
          className="inline-flex items-center gap-1.5 font-sans text-sm text-[#7A7571] hover:text-[#2C2A29] transition-colors disabled:opacity-50"
        >
          {pending === "portal" ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <ExternalLink size={12} strokeWidth={2} />
          )}
          Manage payment method & invoices on Stripe →
        </button>
      ) : null}

      {/* Plan switch confirm */}
      <AlertDialog
        open={!!confirmPlanChange}
        onOpenChange={(o) => !o && setConfirmPlanChange(null)}
      >
        <AlertDialogContent data-testid="billing-confirm-plan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Switch to {confirmPlanChange?.display_name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Your subscription will switch immediately. Stripe will prorate
              the difference for the current billing period. New monthly
              charge: {fmtUsd(confirmPlanChange?.monthly_price_cents)}/mo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="billing-confirm-plan-cancel"
              onClick={() => setConfirmPlanChange(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="billing-confirm-plan-confirm"
              disabled={pending !== null}
              onClick={() => startSubscribe(confirmPlanChange.plan)}
              className="bg-[#2C2A29] text-white hover:bg-[#1A1918]"
            >
              {pending ? (
                <Loader2 size={13} className="animate-spin mr-1" />
              ) : null}
              Switch plan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel confirm */}
      <AlertDialog
        open={confirmCancel}
        onOpenChange={(o) => !o && setConfirmCancel(false)}
      >
        <AlertDialogContent data-testid="billing-confirm-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              Your workspace will revert to the free plan on{" "}
              {fmtDate(stripeState.subscription_current_period_end)}.
              You&apos;ll keep full access until then. Optional reason (audit log):
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            data-testid="billing-cancel-reason-input"
            type="text"
            placeholder="e.g. team restructuring"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
          />
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="billing-confirm-cancel-keep"
              onClick={() => setConfirmCancel(false)}
            >
              Keep subscription
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="billing-confirm-cancel-confirm"
              disabled={pending === "cancel"}
              onClick={startCancel}
              className="bg-[#8C3A1F] text-white hover:bg-[#5E1812]"
            >
              {pending === "cancel" ? (
                <Loader2 size={13} className="animate-spin mr-1" />
              ) : null}
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Topup modal */}
      <AlertDialog
        open={topupOpen}
        onOpenChange={(o) => !o && setTopupOpen(false)}
      >
        <AlertDialogContent
          data-testid="billing-topup-dialog"
          className="max-w-md"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Top up your balance</AlertDialogTitle>
            <AlertDialogDescription>
              Add prepaid credit to this workspace. Charges happen instantly
              via Stripe. Used to cover usage beyond the included allowance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div
              className="grid grid-cols-3 gap-2"
              data-testid="billing-topup-presets"
            >
              {[1000, 2500, 5000, 10000, 25000].map((amt) => {
                const active = topupAmount === amt && !customTopup;
                return (
                  <button
                    key={amt}
                    data-testid={`billing-topup-preset-${amt}`}
                    onClick={() => {
                      setTopupAmount(amt);
                      setCustomTopup("");
                    }}
                    className={`px-3 py-2 rounded-md border font-sans text-sm transition-colors ${
                      active
                        ? "border-[#D96C4A] bg-[#FBE3D3] text-[#5E2812] font-medium"
                        : "border-[#DCD7CE] bg-white text-[#2C2A29] hover:bg-[#FAF9F7]"
                    }`}
                  >
                    {fmtUsd(amt)}
                  </button>
                );
              })}
            </div>
            <div>
              <label className="font-sans text-xs text-[#7A7571] font-medium mb-1 block">
                …or custom amount (USD)
              </label>
              <input
                data-testid="billing-topup-custom-input"
                type="number"
                min={5}
                max={500}
                step={1}
                placeholder="e.g. 75"
                value={customTopup}
                onChange={(e) => setCustomTopup(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
              />
              <p className="font-sans text-[11px] text-[#A5A29E] mt-1">
                Min $5, max $500.
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="billing-topup-cancel"
              onClick={() => setTopupOpen(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="billing-topup-confirm"
              disabled={pending !== null}
              onClick={() => {
                const amt = customTopup
                  ? Math.round(Number(customTopup) * 100)
                  : topupAmount;
                startTopup(amt);
              }}
              className="bg-[#D96C4A] text-white hover:bg-[#8C3A1F]"
            >
              {pending && pending.startsWith("topup-") ? (
                <Loader2 size={13} className="animate-spin mr-1" />
              ) : null}
              Continue to Stripe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_LABEL[status] || { label: status, tone: "muted" };
  const styles = {
    ok: "bg-[#3A5A40]/10 text-[#3A5A40]",
    info: "bg-[#5B4C7A]/10 text-[#5B4C7A]",
    warn: "bg-[#FAF1D1] text-[#5E4012]",
    danger: "bg-[#8C3A1F]/10 text-[#8C3A1F]",
    muted: "bg-[#EFEAE0] text-[#7A7571]",
  };
  return (
    <span
      data-testid={`billing-subscription-status-${status}`}
      className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
        styles[meta.tone] || styles.muted
      }`}
    >
      {meta.label}
    </span>
  );
}

function cleanQueryParam(keys) {
  if (typeof window === "undefined") return;
  const u = new URL(window.location.href);
  let dirty = false;
  for (const k of keys) {
    if (u.searchParams.has(k)) {
      u.searchParams.delete(k);
      dirty = true;
    }
  }
  if (dirty) window.history.replaceState({}, "", u.toString());
}
